import React, { useState, useEffect } from 'react';
import { Map, Award, X, Calendar, Timer, Plus, Trash2, Eye, Mail, Upload, AlertCircle, EyeOff, Sparkles, Copy } from 'lucide-react';
import type { Event, CertificateSettings, SplitPoint, ActionButton } from '../types';
import { supabase } from '../lib/supabase';
import CertificatePreview from './CertificatePreview';

interface Props {
  event: Event;
  onClose: () => void;
  onUpdate: () => void;
}

const SYSTEM_FONTS = {
  'System Default': '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, system-ui, sans-serif',
  'System UI': 'system-ui, sans-serif',
  'SF Pro': '-apple-system, BlinkMacSystemFont, sans-serif',
  'Segoe UI': '"Segoe UI", sans-serif',
  'Inter': 'Inter, system-ui, sans-serif'
};

// Image optimization function
const optimizeImage = (file: File, maxWidth: number = 1200, quality: number = 0.8): Promise<Blob> => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    const img = new Image();
    
    img.onload = () => {
      // Calculate new dimensions
      const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
      const newWidth = img.width * ratio;
      const newHeight = img.height * ratio;
      
      canvas.width = newWidth;
      canvas.height = newHeight;
      
      // Draw and compress
      ctx.drawImage(img, 0, 0, newWidth, newHeight);
      canvas.toBlob(resolve, 'image/jpeg', quality);
    };
    
    img.src = URL.createObjectURL(file);
  });
};

export default function EventSettings({ event, onClose, onUpdate }: Props) {
  const [routeMapUrl, setRouteMapUrl] = useState(event.route_map_url || '');
  const [showRouteMap, setShowRouteMap] = useState(event.route_map_url !== null);
  const [eventDate, setEventDate] = useState(event.date);
  const [eventDistance, setEventDistance] = useState(event.distance?.toString() || '');
  const [contactEmail, setContactEmail] = useState(event.contact_email || '');
  const [enableAiSummary, setEnableAiSummary] = useState(event.enable_ai_summary ?? false);
  const [showSpeedInsteadOfPace, setShowSpeedInsteadOfPace] = useState(event.show_speed_instead_of_pace ?? false);
  const [showFinishOverview, setShowFinishOverview] = useState(event.show_finish_overview ?? false);
  const [showGunTimeInsteadOfChipTime, setShowGunTimeInsteadOfChipTime] = useState(event.show_gun_time_instead_of_chip_time ?? false);
  const [certificateSettings, setCertificateSettings] = useState<CertificateSettings>(
    event.certificate_settings || {
      titleText: 'Certificate of Achievement',
      textColor: '#1e3a8a',
      font: 'System Default',
      backgroundColor: '#ffffff',
      accentColor: '#3b82f6',
      secondaryColor: '#93c5fd',
      logo: '',
      showOnResults: true
    }
  );
  const [splitPoints, setSplitPoints] = useState<SplitPoint[]>([]);
  const [newSplitPoint, setNewSplitPoint] = useState({ name: '', distance: '' });
  const [loading, setLoading] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBackground, setUploadingBackground] = useState(false);
  const [uploadingRouteMap, setUploadingRouteMap] = useState(false);
  const [actionButton, setActionButton] = useState<ActionButton>(
    event.action_button || {
      text: '',
      url: ''
    }
  );

  useEffect(() => {
    loadSplitPoints();
  }, [event.id]);

  const loadSplitPoints = async () => {
    if (!event.id) return;
    
    const { data } = await supabase
      .from('split_points')
      .select('*')
      .eq('event_id', event.id)
      .order('order_index', { ascending: true });

    if (data) {
      setSplitPoints(data);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('events')
        .update({
          route_map_url: showRouteMap ? routeMapUrl : null,
          certificate_settings: certificateSettings,
          date: eventDate,
          distance: eventDistance ? parseFloat(eventDistance) : null,
          action_button: actionButton.text && actionButton.url ? actionButton : null,
          contact_email: contactEmail.trim() || null,
          enable_ai_summary: enableAiSummary,
          show_speed_instead_of_pace: showSpeedInsteadOfPace,
          show_finish_overview: showFinishOverview,
          show_gun_time_instead_of_chip_time: showGunTimeInsteadOfChipTime
        })
        .eq('id', event.id);

      if (updateError) throw updateError;
      onUpdate();
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateEvent = async () => {
    setDuplicating(true);
    setError(null);

    try {
      // 1. Create a copy of the event
      const { data: newEvent, error: eventError } = await supabase
        .from('events')
        .insert({
          name: `${event.name} Copy`,
          date: eventDate,
          user_id: event.user_id,
          organization_id: event.organization_id,
          route_map_url: showRouteMap ? routeMapUrl : null,
          certificate_settings: certificateSettings,
          distance: eventDistance ? parseFloat(eventDistance) : null,
          action_button: actionButton.text && actionButton.url ? actionButton : null,
          contact_email: contactEmail.trim() || null,
          enable_ai_summary: enableAiSummary,
          show_speed_instead_of_pace: showSpeedInsteadOfPace,
          show_finish_overview: showFinishOverview,
          show_gun_time_instead_of_chip_time: showGunTimeInsteadOfChipTime,
          column_config: event.column_config,
          sub_event_config: event.sub_event_config,
          shared_results_background: event.shared_results_background,
          shared_results_logo: event.shared_results_logo,
          timing_company_logo: event.timing_company_logo,
          is_published: event.is_published
        })
        .select()
        .single();

      if (eventError) throw eventError;
      if (!newEvent) throw new Error('Failed to create duplicate event');

      // 2. Copy split points
      if (splitPoints.length > 0) {
        const newSplitPoints = splitPoints.map(point => ({
          event_id: newEvent.id,
          name: point.name,
          distance: point.distance,
          order_index: point.order_index
        }));

        const { error: splitPointsError } = await supabase
          .from('split_points')
          .insert(newSplitPoints);

        if (splitPointsError) throw splitPointsError;
      }

      onUpdate();
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDuplicating(false);
    }
  };

  const handleAddSplitPoint = async () => {
    if (!newSplitPoint.name.trim() || !newSplitPoint.distance.trim()) {
      setError('Split point name and distance are required');
      return;
    }

    try {
      const { error: insertError } = await supabase
        .from('split_points')
        .insert({
          event_id: event.id,
          name: newSplitPoint.name.trim(),
          distance: parseFloat(newSplitPoint.distance),
          order_index: splitPoints.length,
        });

      if (insertError) throw insertError;

      setNewSplitPoint({ name: '', distance: '' });
      loadSplitPoints();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeleteSplitPoint = async (id: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('split_points')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      loadSplitPoints();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleRouteMapUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be smaller than 10MB');
      return;
    }

    setUploadingRouteMap(true);
    setError(null);

    try {
      // Optimize the image before upload
      const optimizedBlob = await optimizeImage(file, 1200, 0.85);
      
      // Remove old route map if it exists and was uploaded
      if (routeMapUrl && routeMapUrl.includes('supabase')) {
        const oldMapPath = routeMapUrl.split('/').pop();
        if (oldMapPath) {
          await supabase.storage
            .from('route-maps')
            .remove([`${event.id}/${oldMapPath}`]);
        }
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${event.id}/route-map-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('route-maps')
        .upload(fileName, optimizedBlob);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('route-maps')
        .getPublicUrl(fileName);

      setRouteMapUrl(publicUrl);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploadingRouteMap(false);
    }
  };

  const handleDeleteRouteMap = async () => {
    if (!routeMapUrl) return;

    setUploadingRouteMap(true);
    setError(null);

    try {
      // Only delete if it's an uploaded file (contains supabase in URL)
      if (routeMapUrl.includes('supabase')) {
        const mapPath = routeMapUrl.split('/').pop();
        if (mapPath) {
          await supabase.storage
            .from('route-maps')
            .remove([`${event.id}/${mapPath}`]);
        }
      }

      setRouteMapUrl('');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploadingRouteMap(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError('Image must be smaller than 2MB');
      return;
    }

    setUploadingLogo(true);
    setError(null);

    try {
      if (certificateSettings.logo) {
        const oldLogoPath = certificateSettings.logo.split('/').pop();
        if (oldLogoPath) {
          await supabase.storage
            .from('certificate-logos')
            .remove([oldLogoPath]);
        }
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${event.id}-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('certificate-logos')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('certificate-logos')
        .getPublicUrl(fileName);

      setCertificateSettings({
        ...certificateSettings,
        logo: publicUrl
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!certificateSettings.logo) return;

    setUploadingLogo(true);
    setError(null);

    try {
      const logoPath = certificateSettings.logo.split('/').pop();
      if (logoPath) {
        await supabase.storage
          .from('certificate-logos')
          .remove([logoPath]);
      }

      setCertificateSettings({
        ...certificateSettings,
        logo: ''
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleBackgroundUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Background image must be smaller than 5MB');
      return;
    }

    setUploadingBackground(true);
    setError(null);

    try {
      if (certificateSettings.background?.url) {
        const oldBgPath = certificateSettings.background.url.split('/').pop();
        if (oldBgPath) {
          await supabase.storage
            .from('certificate-backgrounds')
            .remove([oldBgPath]);
        }
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${event.id}-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('certificate-backgrounds')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('certificate-backgrounds')
        .getPublicUrl(fileName);

      setCertificateSettings({
        ...certificateSettings,
        background: {
          url: publicUrl,
          opacity: 0.15
        }
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploadingBackground(false);
    }
  };

  const handleRemoveBackground = async () => {
    if (!certificateSettings.background?.url) return;

    setUploadingBackground(true);
    setError(null);

    try {
      const bgPath = certificateSettings.background.url.split('/').pop();
      if (bgPath) {
        await supabase.storage
          .from('certificate-backgrounds')
          .remove([bgPath]);
      }

      setCertificateSettings({
        ...certificateSettings,
        background: undefined
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploadingBackground(false);
    }
  };

  const isUploadedImage = routeMapUrl && routeMapUrl.includes('supabase');
  const defaultMapUrl = "https://images.unsplash.com/photo-1569336415962-a4bd9f69cd83?auto=format&fit=crop&q=80&w=1000";

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-[calc(100vh-2rem)] flex items-center justify-center w-full">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl my-4 border border-gray-200 overflow-hidden">
          <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-white">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Event Settings</h2>
              <p className="text-gray-600 mt-1">Configure your event details and customization options</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDuplicateEvent}
                disabled={duplicating}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                <Copy className="w-4 h-4" />
                {duplicating ? 'Duplicating...' : 'Duplicate Event'}
              </button>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-6 overflow-y-auto flex-1 max-h-[calc(100vh-16rem)] space-y-8 bg-white">
            {error && (
              <div className="bg-red-50 text-red-600 p-4 rounded-lg flex items-center gap-2 border border-red-200">
                <AlertCircle className="w-5 h-5" />
                {error}
              </div>
            )}

            {/* Event Details Section */}
            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-1">Event Details</h3>
              <p className="text-gray-600 mb-6">Basic information about your event</p>
              
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date
                    </label>
                    <input
                      type="date"
                      value={eventDate}
                      onChange={(e) => setEventDate(e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Total Distance (km)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={eventDistance}
                      onChange={(e) => setEventDistance(e.target.value)}
                      placeholder="e.g., 42.195 for marathon"
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500"
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Display Settings Section */}
            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-1">Display Settings</h3>
              <p className="text-gray-600 mb-6">Configure how results are displayed</p>
              
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-4">
                <div className="flex items-center gap-3 p-4 bg-white rounded-lg border border-gray-200">
                  <Timer className="w-5 h-5 text-gray-600" />
                  <div className="flex-1">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showSpeedInsteadOfPace}
                        onChange={(e) => setShowSpeedInsteadOfPace(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      <span className="ms-3 text-sm font-medium text-gray-700">
                        Show Speed Instead of Pace
                      </span>
                    </label>
                    <p className="mt-1 text-sm text-gray-500">
                      When enabled, split times will show speed (km/h) instead of pace (min/km)
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-white rounded-lg border border-gray-200">
                  <Eye className="w-5 h-5 text-gray-600" />
                  <div className="flex-1">
                    <div className="flex items-center">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={showFinishOverview}
                          onChange={(e) => setShowFinishOverview(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        <span className="ms-3 text-sm font-medium text-gray-700">
                          Show Finish Overview
                        </span>
                      </label>
                      <span className="ml-2 px-1.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                        BETA
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-500">
                      When enabled, participants can see nearby finishers in the results modal
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-white rounded-lg border border-gray-200">
                  <Timer className="w-5 h-5 text-gray-600" />
                  <div className="flex-1">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showGunTimeInsteadOfChipTime}
                        onChange={(e) => setShowGunTimeInsteadOfChipTime(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      <span className="ms-3 text-sm font-medium text-gray-700">
                        Show Gun Time Instead of Chip Time
                      </span>
                    </label>
                    <p className="mt-1 text-sm text-gray-500">
                      When enabled, gun time is displayed as the primary time instead of chip time
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* AI Summary Section */}
            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-1">AI Summary</h3>
              <p className="text-gray-600 mb-6">Generate personalized AI-powered summaries for participant results</p>
              
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="flex items-center gap-3 p-4 bg-white rounded-lg border border-gray-200">
                  <Sparkles className="w-5 h-5 text-blue-500" />
                  <div className="flex-1">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={enableAiSummary}
                        onChange={(e) => setEnableAiSummary(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      <span className="ms-3 text-sm font-medium text-gray-700">
                        Enable AI Result Summary
                      </span>
                    </label>
                    <p className="mt-1 text-sm text-gray-500">
                      When enabled, a personalized AI-generated summary will appear on each participant's result modal, highlighting their achievements and performance.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Split Points Section */}
            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-1">Split Points</h3>
              <p className="text-gray-600 mb-6">Configure timing checkpoints for your event</p>
              
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-4">
                {splitPoints.map((point, index) => (
                  <div key={point.id} className="flex items-center gap-4 bg-white p-3 rounded-md border border-gray-200">
                    <span className="text-gray-500 text-sm">{index + 1}</span>
                    <div className="flex-1">
                      <p className="font-medium">{point.name}</p>
                      <p className="text-sm text-gray-600">{point.distance}km</p>
                    </div>
                    <button
                      onClick={() => handleDeleteSplitPoint(point.id)}
                      className="text-gray-400 hover:text-red-600 p-2 rounded-full hover:bg-red-50 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}

                <div className="flex gap-2">
                  <div className="flex-1">
                    <input
                      type="text"
                      placeholder="Split point name"
                      value={newSplitPoint.name}
                      onChange={(e) => setNewSplitPoint({ ...newSplitPoint, name: e.target.value })}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500"
                    />
                  </div>
                  <div className="w-32">
                    <input
                      type="number"
                      step="0.1"
                      placeholder="Distance (km)"
                      value={newSplitPoint.distance}
                      onChange={(e) => setNewSplitPoint({ ...newSplitPoint, distance: e.target.value })}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500"
                    />
                  </div>
                  <button
                    onClick={handleAddSplitPoint}
                    className="px-3 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </section>

            {/* Route Map Section */}
            <section>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Route Map</h3>
                  <p className="text-gray-600">Display a course map on the results page</p>
                </div>
                <button
                  onClick={() => setShowRouteMap(!showRouteMap)}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
                >
                  {showRouteMap ? (
                    <>
                      <Eye className="w-4 h-4" />
                      Visible
                    </>
                  ) : (
                    <>
                      <EyeOff className="w-4 h-4" />
                      Hidden
                    </>
                  )}
                </button>
              </div>
              
              {showRouteMap && (
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Map Image URL
                      </label>
                      <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full font-medium">
                        Best
                      </span>
                    </div>
                    <input
                      type="url"
                      value={routeMapUrl}
                      onChange={(e) => setRouteMapUrl(e.target.value)}
                      placeholder="https://example.com/route-map.jpg"
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500"
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Recommended: Use a CDN or image hosting service for best performance.
                    </p>
                  </div>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-gray-50 text-gray-500">or</span>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Upload Route Map
                      </label>
                      <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full font-medium">
                        Alternative
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleRouteMapUpload}
                        disabled={uploadingRouteMap}
                        className="flex-1 px-3 py-2 text-sm text-gray-500
                          file:mr-4 file:py-2 file:px-4
                          file:rounded-md file:border-0
                          file:text-sm file:font-semibold
                          file:bg-gray-100 file:text-gray-700
                          hover:file:bg-gray-200 disabled:opacity-50"
                      />
                      {isUploadedImage && (
                        <button
                          onClick={handleDeleteRouteMap}
                          disabled={uploadingRouteMap}
                          className="p-2 text-red-600 hover:text-red-700 disabled:opacity-50"
                          title="Delete uploaded image"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                      {uploadingRouteMap && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                          Optimizing...
                        </div>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-gray-500">
                      Maximum file size: 10MB. Images will be optimized for web automatically.
                    </p>
                    {isUploadedImage && (
                      <p className="mt-1 text-sm text-green-600">
                        ✓ Image uploaded successfully
                      </p>
                    )}
                  </div>

                  {(routeMapUrl || !routeMapUrl) && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-gray-700">Preview</h4>
                      <div className="relative w-full bg-gray-100 rounded-md overflow-hidden">
                        <img
                          src={routeMapUrl || defaultMapUrl}
                          alt="Route Map Preview"
                          className="w-full h-auto object-contain"
                          onError={(e) => {
                            e.currentTarget.src = defaultMapUrl;
                          }}
                        />
                        {!routeMapUrl && (
                          <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                            <p className="text-white text-sm font-medium">Preview - Add your route map above</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* Action Button Section */}
            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-1">Action Button</h3>
              <p className="text-gray-600 mb-6">Add a custom button to the results modal</p>
              
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Button Text
                  </label>
                  <input
                    type="text"
                    value={actionButton.text}
                    onChange={(e) => setActionButton({
                      ...actionButton,
                      text: e.target.value
                    })}
                    placeholder="e.g., Find My Photos, View Merchandise, Enter Next Race"
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    This text will appear on the button in the results modal
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Button URL
                  </label>
                  <input
                    type="url"
                    value={actionButton.url}
                    onChange={(e) => setActionButton({
                      ...actionButton,
                      url: e.target.value
                    })}
                    placeholder="https://example.com/photos"
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Where should participants be directed when clicking the button?
                  </p>
                </div>
              </div>
            </section>

            {/* Contact Information Section */}
            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-1">Contact Information</h3>
              <p className="text-gray-600 mb-6">Email for participant inquiries about results</p>
              
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Email for Results Inquiries
                </label>
                <input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="e.g., results@myevent.com"
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500"
                />
                <p className="mt-1 text-sm text-gray-500">
                  This email will be used for participants to send inquiries about their results
                </p>
              </div>
            </section>

            {/* Certificate Design Section */}
            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-1">Certificate Design</h3>
              <p className="text-gray-600 mb-6">Customize downloadable certificates for participants</p>

              <div className="space-y-6">
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <Eye className="w-5 h-5 text-gray-500" />
                  <div className="flex-1">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={certificateSettings.showOnResults ?? true}
                        onChange={(e) => setCertificateSettings({
                          ...certificateSettings,
                          showOnResults: e.target.checked
                        })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      <span className="ms-3 text-sm font-medium text-gray-700">
                        Show certificate on results page
                      </span>
                    </label>
                    <p className="mt-1 text-sm text-gray-500">
                      When enabled, participants can download their certificate directly from the results page
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium text-gray-700">Live Preview</h4>
                    <div className="text-sm text-gray-500">Sample certificate design</div>
                  </div>
                  <CertificatePreview 
                    event={event}
                    settings={certificateSettings}
                  />
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Logo
                    </label>
                    <div className="space-y-4">
                      {certificateSettings.logo && (
                        <div className="flex items-center gap-4">
                          <div className="h-16 w-32 bg-white rounded-md overflow-hidden border border-gray-200">
                            <img
                              src={certificateSettings.logo}
                              alt="Logo Preview"
                              className="h-full w-full object-contain"
                            />
                          </div>
                          <button
                            onClick={handleRemoveLogo}
                            disabled={uploadingLogo}
                            className="text-red-600 hover:text-red-700 disabled:opacity-50 p-2 rounded-full hover:bg-red-50 transition-colors"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      )}
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          disabled={uploadingLogo}
                          className="w-full px-3 py-2 text-sm text-gray-500
                            file:mr-4 file:py-2 file:px-4
                            file:rounded-md file:border-0
                            file:text-sm file:font-semibold
                            file:bg-gray-100 file:text-gray-700
                            hover:file:bg-gray-200"
                        />
                        <p className="mt-1 text-sm text-gray-500">
                          Maximum file size: 2MB. Supported formats: PNG, JPG, GIF
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Background Image
                    </label>
                    <div className="space-y-4">
                      {certificateSettings.background?.url && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-4">
                            <div className="h-24 w-full bg-white rounded-md overflow-hidden border border-gray-200">
                              <img
                                src={certificateSettings.background.url}
                                alt="Certificate Background Preview"
                                className="h-full w-full object-cover"
                              />
                            </div>
                            <button
                              onClick={handleRemoveBackground}
                              disabled={uploadingBackground}
                              className="text-red-600 hover:text-red-700 disabled:opacity-50 p-2 rounded-full hover:bg-red-50 transition-colors"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Background Opacity
                            </label>
                            <div className="flex items-center gap-4">
                              <Eye className="w-4 h-4 text-gray-400" />
                              <div className="flex-1">
                                <input
                                  type="range"
                                  min="0"
                                  max="100"
                                  value={Math.round(certificateSettings.background.opacity * 100)}
                                  onChange={(e) => setCertificateSettings({
                                    ...certificateSettings,
                                    background: {
                                      ...certificateSettings.background!,
                                      opacity: parseInt(e.target.value) / 100
                                    }
                                  })}
                                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-blue-600 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:appearance-none [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:bg-blue-600 [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:rounded-full hover:[&::-webkit-slider-thumb]:bg-blue-700 hover:[&::-moz-range-thumb]:bg-blue-700 focus:[&::-webkit-slider-thumb]:ring-2 focus:[&::-webkit-slider-thumb]:ring-blue-200 focus:[&::-moz-range-thumb]:ring-2 focus:[&::-moz-range-thumb]:ring-blue-200"
                                />
                              </div>
                              <div className="w-12 text-sm text-gray-600 tabular-nums">
                                {Math.round(certificateSettings.background.opacity * 100)}%
                              </div>
                            </div>
                            <div className="flex justify-between text-xs text-gray-500 px-1">
                              <span>Transparent</span>
                              <span>Solid</span>
                            </div>
                          </div>
                        </div>
                      )}
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleBackgroundUpload}
                          disabled={uploadingBackground}
                          className="w-full px-3 py-2 text-sm text-gray-500
                            file:mr-4 file:py-2 file:px-4
                            file:rounded-md file:border-0
                            file:text-sm file:font-semibold
                            file:bg-gray-100 file:text-gray-700
                            hover:file:bg-gray-200"
                        />
                        <p className="mt-1 text-sm text-gray-500">
                          Maximum file size: 5MB. Supported formats: PNG, JPG, GIF
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Title Text
                      </label>
                      <input
                        type="text"
                        value={certificateSettings.titleText}
                        onChange={(e) => setCertificateSettings({
                          ...certificateSettings,
                          titleText: e.target.value
                        })}
                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Font Family
                      </label>
                      <select
                        value={certificateSettings.font}
                        onChange={(e) => setCertificateSettings({
                          ...certificateSettings,
                          font: e.target.value
                        })}
                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500"
                      >
                        {Object.entries(SYSTEM_FONTS).map(([name]) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Background Color
                      </label>
                      <input
                        type="color"
                        value={certificateSettings.backgroundColor}
                        onChange={(e) => setCertificateSettings({
                          ...certificateSettings,
                          backgroundColor: e.target.value
                        })}
                        className="w-full h-10"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Text Color
                      </label>
                      <input
                        type="color"
                        value={certificateSettings.textColor}
                        onChange={(e) => setCertificateSettings({
                          ...certificateSettings,
                          textColor: e.target.value
                        })}
                        className="w-full h-10"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Accent Color
                      </label>
                      <input
                        type="color"
                        value={certificateSettings.accentColor}
                        onChange={(e) => setCertificateSettings({
                          ...certificateSettings,
                          accentColor: e.target.value
                        })}
                        className="w-full h-10"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Secondary Color
                      </label>
                      <input
                        type="color"
                        value={certificateSettings.secondaryColor}
                        onChange={(e) => setCertificateSettings({
                          ...certificateSettings,
                          secondaryColor: e.target.value
                        })}
                        className="w-full h-10"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <div className="p-6 border-t border-gray-200 bg-gray-50">
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}